import {
  ChangePasswordResponse,
  LoginResponse,
  ReauthResponse,
  SessionInfo,
  TotpSetupResponse,
} from '@bartizo/shared';
import { z } from 'zod';
import { apiRequest } from './client.js';

/**
 * `/v1/auth/*` as the dashboard sees it (knowledge doc sections 15 and 17).
 *
 * No token is returned or stored: the session is an `HttpOnly` cookie the browser
 * carries by itself, so signing in is a call whose only visible result is that the
 * next call works.
 */

/**
 * `totpCode` is left out on the first attempt. When the account has an
 * authenticator the API answers `totp_required`, and the dashboard asks for the
 * code and calls this again.
 */
export const login = (email: string, password: string, totpCode?: string): Promise<LoginResponse> =>
  apiRequest('/auth/login', {
    method: 'POST',
    body: { email, password, ...(totpCode === undefined ? {} : { totpCode }) },
    schema: LoginResponse,
  });

/**
 * Spends the one-time link `install.sh central` printed (P8.1): the first account,
 * an admin, signed in by the same call.
 */
export const setupAdmin = (
  token: string,
  email: string,
  password: string,
): Promise<LoginResponse> =>
  apiRequest('/auth/setup', {
    method: 'POST',
    body: { token, email, password },
    schema: LoginResponse,
  });

export const logout = (): Promise<{ ok: true }> =>
  apiRequest('/auth/logout', {
    method: 'POST',
    schema: z.object({ ok: z.literal(true) }),
  });

/** Section 17's re-check: one of the two, never both. */
export const reauth = (
  credential: { password: string } | { totpCode: string },
): Promise<ReauthResponse> =>
  apiRequest('/auth/reauth', {
    method: 'POST',
    body: credential,
    schema: ReauthResponse,
  });

/** Section 20's display time zone; `null` goes back to the installation default. */
export const updatePreferences = (timezone: string | null): Promise<SessionInfo> =>
  apiRequest('/auth/me', {
    method: 'PATCH',
    body: { timezone },
    schema: SessionInfo,
  });

/** Restores the session on a page load; 401 means "not signed in", not "broken". */
export const fetchSession = (signal?: AbortSignal): Promise<SessionInfo> =>
  apiRequest('/auth/me', { schema: SessionInfo, ...(signal === undefined ? {} : { signal }) });

/** Issues a secret that is not yet in force. Needs a recent re-check. */
export const totpSetup = (): Promise<TotpSetupResponse> =>
  apiRequest('/auth/totp/setup', { method: 'POST', schema: TotpSetupResponse });

/** Turns it on, once a code proves the app holds the secret. */
export const totpEnable = (totpCode: string): Promise<SessionInfo> =>
  apiRequest('/auth/totp/enable', { method: 'POST', body: { totpCode }, schema: SessionInfo });

export const totpDisable = (totpCode: string): Promise<SessionInfo> =>
  apiRequest('/auth/totp/disable', { method: 'POST', body: { totpCode }, schema: SessionInfo });

export const changePassword = (
  currentPassword: string,
  newPassword: string,
): Promise<ChangePasswordResponse> =>
  apiRequest('/auth/password', {
    method: 'POST',
    body: { currentPassword, newPassword },
    schema: ChangePasswordResponse,
  });
